var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
// External dependencies
import { useState, useContext } from 'react';
import { Box, Paper, Typography, Grid } from '@mui/material';
import { Add } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
// Internal dependencies
import { axiosClient } from '../axios';
import { getAuthHeader, UserContext } from '../auth';
import { LOG_LEVELS, SERVICES, ENDPOINTS } from '../constants';
import { appStyles } from '../App.styles';
import { UploadFileButtonSG, DeleteConfirmButtonSG } from '../components';
import { InfobaseTableSG } from './InfobaseTableSG';
import { InfobaseSubtitle } from './InfobaseSubtitle';
import { IMAGE_FILE_TYPES, TEXT_FILE_TYPES } from './constants';
/**
 * Infobase Component
 * @param props Props
 * @returns JSX Element
 */
export function Infobase(props) {
    const { /** Translation function */ t, /** Translation module */ i18n } = useTranslation();
    const /** User credential */ user = useContext(UserContext);
    const [
    /** Delete Files */ deleteFiles, 
    /** DeleteFiles setter */ setDeleteFiles] = useState([]);
    const [
    /** Reload Infobase files flag */ reloadInfobaseFilesFlag, 
    /** reloadInfobaseFiles setter */ setReloadInfobaseFilesFlag] = useState(0);
    const [/** Uploading status */ uploadLoading, /** uploadLoading setter */ setUploadLoading] = useState(false);
    /** Handle Delete confirm click */
    const handleDeleteConfirmClick = () => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        try {
            // Set headers
            const /** Auth headers */ authHeader = yield getAuthHeader(user);
            const /** Request headers */ headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
            // Get delete files ids
            const deleteFileIds = deleteFiles.map((deleteFile) => (deleteFile === null || deleteFile === void 0 ? void 0 : deleteFile._id));
            // Call Delete Files APIs
            const res = yield axiosClient.delete(`${process.env.SERVER_URL}${ENDPOINTS.INFOBASE}${ENDPOINTS.DELETE_FILES}`, { headers, data: { fileIds: deleteFileIds } });
            setDeleteFiles([]);
            setReloadInfobaseFilesFlag(reloadInfobaseFilesFlag + 1);
            return true;
        }
        catch (err) {
            setDeleteFiles([]);
            setReloadInfobaseFilesFlag(reloadInfobaseFilesFlag + 1);
            // Log error
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Infobase, handleDeleteConfirmClick, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.DOCUMENTS,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            if ((_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.status) {
                // If the error is from the server response
                // notify user with the server message
                toast.error((_c = (_b = err.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message);
                return false;
            }
            toast.error(t('Something went wrong. We are figuring it out.'));
            return false;
        }
        ;
    });
    const [
    /** Supported attach file types by AI model */ attachFileTypes, setAttachFileTypes] = useState([...TEXT_FILE_TYPES, ...IMAGE_FILE_TYPES]);
    return (_jsxs(Paper, Object.assign({ sx: Object.assign({}, appStyles.page) }, { children: [_jsx(Box, Object.assign({ sx: {
                    backgroundColor: 'white.main',
                    padding: '0',
                    zIndex: '2',
                } }, { children: _jsxs(Grid, Object.assign({ container: true, columnSpacing: 1, sx: { padding: '0', alignItems: 'center' } }, { children: [_jsx(Grid, Object.assign({ item: true, xs: true, sx: { overflow: 'hidden', display: 'flex' } }, { children: _jsx(Typography, Object.assign({ variant: "text28", component: "h1", sx: {
                                    color: 'black.main',
                                    overflow: 'hidden',
                                    width: '100%',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                } }, { children: t('Infobase') })) })), _jsx(Grid, Object.assign({ item: true }, { children: _jsx(UploadFileButtonSG, { variant: 'contained', styles: { display: 'flex', alignItems: 'center' }, reloadInfobaseFilesFlag: reloadInfobaseFilesFlag, setReloadInfobaseFilesFlag: setReloadInfobaseFilesFlag, uploadLoading: uploadLoading, setUploadLoading: setUploadLoading, startIconSG: _jsx(Add, { sx: { color: 'white.main' } }), attachFileTypes: attachFileTypes }) })), _jsx(Grid, Object.assign({ item: true }, { children: _jsx(DeleteConfirmButtonSG, { disabled: deleteFiles.length > 0 ? false : true, handleDeleteConfirmClick: handleDeleteConfirmClick }) }))] })) })), _jsxs(Box, Object.assign({ sx: {
                    height: 'calc(100% - 40px)',
                    overflow: 'auto',
                    padding: '1.5rem 0 1px',
                } }, { children: [_jsx(InfobaseSubtitle, { styles: {
                            backgroundColor: 'blueLight.main',
                            margin: '0 0 1rem',
                            display: uploadLoading ? 'block' : 'none'
                        }, text: 'The file is processing. This may take a few minutes. You can attach later when it is ready.' }), _jsx(InfobaseSubtitle, { styles: {
                            backgroundColor: 'greenLight.main',
                            margin: '0 0 1rem',
                            display: {
                                xs: 'none',
                                sm: 'block',
                            }
                        }, text: 'Add files and let AI use them to answer questions and write based on facts.' }), _jsx(Box, Object.assign({ sx: {
                            height: '100%',
                            width: 'auto',
                        } }, { children: _jsx(InfobaseTableSG, { selectedFiles: deleteFiles, setSelectedFiles: setDeleteFiles, reloadInfobaseFilesFlag: reloadInfobaseFilesFlag, attachFileTypes: attachFileTypes }) }))] }))] })));
}
;
